/**
 * Keep the active rail row on screen.
 *
 * The rail is taller than most windows, so a page whose row sits below the
 * fold (Visitors, Platform) left the admin with no highlighted row in view and
 * no way to tell where they were. This scrolls the rail's OWN scroller.
 *
 * `element.scrollIntoView()` is deliberately not used. It scrolls every
 * scrollable ancestor, the document included, so a rail fix would jump the page.
 * Setting `scrollTop` on the one container that owns the overflow cannot.
 *
 * Not the tab-strip scroll that `app-shell.component.ts` removed: that one
 * scrolled page content on `NavigationEnd`. This one touches only the rail.
 */

/**
 * Selector for the scrolling body inside `paper-workspace-rail`. Paper's
 * internal class names are not API (docs/component-tokens.md); this one is
 * read, never styled, and the reveal degrades to doing nothing if it moves.
 */
export const RAIL_SCROLLER_SELECTOR = '.paper-workspace-rail__body';

/**
 * The highlighted row. Paper puts `is-active` + `aria-current="page"` on the
 * active leaf while its parent is open, and on the PARENT while it is closed
 * (by hand, or in the collapsed strip), so there is always exactly one row to
 * find while the active page is in the rail at all.
 */
export const ACTIVE_RAIL_ROW_SELECTOR =
  '.paper-workspace-rail__item.is-active, .paper-workspace-rail__item[aria-current="page"]';

/** Any row a keyboard can land on. */
export const RAIL_ROW_SELECTOR = '.paper-workspace-rail__item:not([aria-disabled="true"])';

/** True when the row is fully inside the scroller's visible box. */
export function isRowFullyVisible(scroller: Element, row: Element): boolean {
  const box = scroller.getBoundingClientRect();
  const r = row.getBoundingClientRect();
  return r.top >= box.top && r.bottom <= box.bottom;
}

/**
 * The `scrollTop` that centres `row` in `scroller`, clamped to the scroll
 * range. Centred rather than `nearest` so the rows around it — its section
 * heading, its siblings — come into view with it.
 */
export function centredScrollTop(scroller: HTMLElement, row: Element): number {
  const box = scroller.getBoundingClientRect();
  const r = row.getBoundingClientRect();
  const rowTopInContent = r.top - box.top + scroller.scrollTop;
  const target = rowTopInContent - (scroller.clientHeight - r.height) / 2;
  const max = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
  return Math.round(Math.min(max, Math.max(0, target)));
}

/**
 * Scroll `row` into view inside `scroller` if any part of it is hidden.
 * Returns whether it scrolled. A row already fully visible is left alone —
 * moving a rail the person can already read is its own disorientation.
 */
export function revealRailRow(
  scroller: HTMLElement,
  row: Element,
  behavior: ScrollBehavior,
): boolean {
  if (isRowFullyVisible(scroller, row)) return false;
  const top = centredScrollTop(scroller, row);
  if (typeof scroller.scrollTo === 'function') scroller.scrollTo({ top, behavior });
  else scroller.scrollTop = top;
  return true;
}

/** Smooth, unless this is the first reveal or the person asked for less motion. */
export function railScrollBehavior(firstReveal: boolean, reducedMotion: boolean): ScrollBehavior {
  return firstReveal || reducedMotion ? 'auto' : 'smooth';
}

/** `prefers-reduced-motion: reduce`, false where matchMedia is missing. */
export function prefersReducedMotion(): boolean {
  try {
    return globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  } catch {
    return false;
  }
}
